import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useWallet } from '../../components/WalletProvider'
import { useDemo } from '../../components/DemoProvider'
import { useNotification } from '../../components/NotificationSystem'
import ProjectCard from '../../components/Projects/ProjectCard'
import ProjectDetails from '../../components/Projects/ProjectDetails'

const Container = styled.div`
  min-height: 100vh;
  padding: 2rem 4rem;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.95) 0%,
    rgba(0, 0, 0, 0.8) 100%
  );
  position: relative;
  overflow-x: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 50% 0%,
      rgba(59, 130, 246, 0.1) 0%,
      transparent 50%
    );
    pointer-events: none;
  }
`

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`

const Title = styled.h1`
  font-size: 2.5rem;
  background: linear-gradient(135deg, var(--color-text) 0%, var(--color-primary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`

const Controls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`

const SearchBar = styled.div`
  position: relative;
  width: 300px;

  svg {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    color: var(--color-text-secondary);
  }
`

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 3rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--color-text);
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }

  &::placeholder {
    color: var(--color-text-secondary);
  }
`

const ViewToggle = styled.div`
  display: flex;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.25rem;
  border-radius: 6px;
`

const ToggleButton = styled.button`
  padding: 0.5rem;
  border-radius: 4px;
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  color: ${props => props.$active ? 'var(--color-text)' : 'var(--color-text-secondary)'};
  transition: all 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    color: var(--color-text);
  }
`

const FilterBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`

const FilterSelect = styled.select`
  padding: 0.5rem 2rem 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: var(--color-text);
  font-size: 0.875rem;
  cursor: pointer;

  option {
    background: #0a0b0f;
  }
`

const ProjectsGrid = styled.div`
  display: grid;
  gap: 2rem;
  grid-template-columns: ${props => props.$view === 'grid' ? 'repeat(auto-fill, minmax(300px, 1fr))' : '1fr'};
`

const CategoryTitle = styled.h2`
  font-size: 1.5rem;
  color: var(--color-text);
  margin: 2rem 0 1rem 0;
`

const WalletRequired = styled.div`
  text-align: center;
  padding: 4rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);

  h3 {
    font-size: 1.5rem;
    color: var(--color-text);
    margin-bottom: 1rem;
  }

  p {
    color: var(--color-text-secondary);
    margin-bottom: 2rem;
  }
`

const ConnectButton = styled.button`
  background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
  color: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`

// Mock data - replace with actual data fetching
const mockProjects = [
  {
    id: 1,
    name: 'Social Media Dashboard',
    type: 'x_management',
    status: 'active',
    date: '2024-01-15',
    description: 'AI-powered social media management platform with real-time analytics and automated content scheduling.',
    agents: { coders: 3, artists: 1, social: 2 },
    links: {
      github: 'https://github.com/example/social-dashboard',
      website: 'https://dashboard.example.com',
      social: 'https://x.com/dashboard'
    },
    activeAgents: [
      { type: 'coder', name: 'CodeBot-1', currentTask: 'Implementing analytics dashboard' },
      { type: 'artist', name: 'DesignBot-1', currentTask: 'Creating data visualizations' },
      { type: 'social', name: 'SocialBot-1', currentTask: 'Analyzing engagement metrics' }
    ],
    activityLog: [
      { timestamp: '2024-01-15T10:30:00', message: 'Deployed new analytics module' },
      { timestamp: '2024-01-15T11:15:00', message: 'Updated engagement tracking algorithm' },
      { timestamp: '2024-01-15T12:00:00', message: 'Generated weekly performance report' }
    ]
  },
  {
    id: 2,
    name: 'E-commerce Website',
    type: 'website',
    status: 'active',
    date: '2024-01-14',
    description: 'Modern e-commerce platform with AI-powered product recommendations and dynamic pricing.',
    agents: { coders: 5, artists: 2, social: 1 },
    links: {
      github: 'https://github.com/example/ecommerce',
      website: 'https://shop.example.com'
    },
    activeAgents: [
      { type: 'coder', name: 'CodeBot-2', currentTask: 'Optimizing checkout process' },
      { type: 'artist', name: 'DesignBot-2', currentTask: 'Redesigning product cards' }
    ],
    activityLog: [
      { timestamp: '2024-01-14T15:20:00', message: 'Implemented new product recommendation algorithm' },
      { timestamp: '2024-01-14T16:45:00', message: 'Updated mobile responsive design' }
    ]
  },
  {
    id: 3,
    name: 'DeFi Protocol',
    type: 'github',
    status: 'completed',
    date: '2024-01-10',
    description: 'Decentralized finance protocol with automated market making and yield optimization.',
    agents: { coders: 4, artists: 1, social: 1 },
    links: {
      github: 'https://github.com/example/defi-protocol',
      website: 'https://defi.example.com'
    },
    activeAgents: [],
    activityLog: [
      { timestamp: '2024-01-10T09:00:00', message: 'Successfully deployed smart contracts' },
      { timestamp: '2024-01-10T10:30:00', message: 'Completed security audit' }
    ]
  },
  {
    id: 4,
    name: 'Brand Growth Campaign',
    type: 'x_management',
    status: 'draft',
    date: '2024-01-16',
    description: 'Comprehensive social media campaign focusing on brand awareness and community engagement.',
    agents: { coders: 1, artists: 2, social: 3 },
    links: {},
    activeAgents: [],
    activityLog: []
  },
  {
    id: 5,
    name: 'NFT Marketplace',
    type: 'website',
    status: 'completed',
    date: '2024-01-08',
    description: 'Custom NFT marketplace with advanced filtering and bidding capabilities.',
    agents: { coders: 4, artists: 3, social: 1 },
    links: {
      github: 'https://github.com/example/nft-market',
      website: 'https://nft.example.com'
    },
    activeAgents: [],
    activityLog: [
      { timestamp: '2024-01-08T14:20:00', message: 'Marketplace launch successful' },
      { timestamp: '2024-01-08T15:45:00', message: 'First NFT collection listed' }
    ]
  },
  {
    id: 6,
    name: 'Community Forum',
    type: 'website',
    status: 'draft',
    date: '2024-01-17',
    description: 'Modern community forum with AI-powered moderation and content recommendations.',
    agents: { coders: 3, artists: 1, social: 2 },
    links: {},
    activeAgents: [],
    activityLog: []
  },
  {
    id: 7,
    name: 'Analytics SDK',
    type: 'github',
    status: 'active',
    date: '2024-01-13',
    description: 'Developer SDK for integrating advanced analytics and tracking capabilities.',
    agents: { coders: 4, artists: 0, social: 0 },
    links: {
      github: 'https://github.com/example/analytics-sdk'
    },
    activeAgents: [
      { type: 'coder', name: 'CodeBot-3', currentTask: 'Implementing event tracking' },
      { type: 'coder', name: 'CodeBot-4', currentTask: 'Writing documentation' }
    ],
    activityLog: [
      { timestamp: '2024-01-13T11:30:00', message: 'Released beta version' },
      { timestamp: '2024-01-13T13:15:00', message: 'Started documentation generation' }
    ]
  }
]

function Projects() {
  const { wallet, openModal } = useWallet()
  const { isDemoMode } = useDemo()
  const [view, setView] = useState('grid')
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all',
    date: 'newest'
  })
  const [selectedProject, setSelectedProject] = useState(null)
  const [projects, setProjects] = useState([])

  useEffect(() => {
    if (wallet.isConnected || isDemoMode) {
      // In demo mode or when wallet is connected, show mock projects
      setProjects(mockProjects)
    }
  }, [wallet.isConnected, isDemoMode])

  const handleSearch = (e) => {
    setSearch(e.target.value)
  }

  const handleFilterChange = (e) => {
    setFilters(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(search.toLowerCase())
    const matchesType = filters.type === 'all' || project.type === filters.type
    const matchesStatus = filters.status === 'all' || project.status === filters.status
    return matchesSearch && matchesType && matchesStatus
  })

  const categorizedProjects = {
    active: filteredProjects.filter(p => p.status === 'active'),
    completed: filteredProjects.filter(p => p.status === 'completed'),
    draft: filteredProjects.filter(p => p.status === 'draft')
  }

  const { addNotification } = useNotification()

  const handleProjectAction = (action) => {
    switch (action) {
      case 'pause':
        addNotification({
          type: 'info',
          title: 'Project Paused',
          message: 'Project has been paused. Agents will complete current tasks before stopping.'
        })
        break
      case 'resume':
        addNotification({
          type: 'success',
          title: 'Project Resumed',
          message: 'Project has been resumed. Agents are now active.'
        })
        break
      case 'archive':
        addNotification({
          type: 'success',
          title: 'Project Archived',
          message: 'Project has been moved to archives.'
        })
        break
      case 'duplicate':
        addNotification({
          type: 'success',
          title: 'Project Duplicated',
          message: 'A new project has been created with the same configuration.'
        })
        break
      case 'delete':
        addNotification({
          type: 'info',
          title: 'Project Deleted',
          message: 'Draft project has been deleted.'
        })
        break
      case 'deploy':
        addNotification({
          type: 'success',
          title: 'Project Deployed',
          message: 'Project is now active and agents are being initialized.'
        })
        break
      case 'view-output':
        addNotification({
          type: 'info',
          title: 'Loading Project Output',
          message: 'Fetching latest project deliverables and outputs.'
        })
        break
      default:
        console.log('Project action:', action)
    }
  }

  const handleViewChange = (newView) => {
    setView(newView)
    addNotification({
      type: 'info',
      title: 'View Changed',
      message: `Switched to ${newView} view`,
      duration: 2000
    })
  }

  useEffect(() => {
    if (wallet.isConnected) {
      addNotification({
        type: 'success',
        title: 'Wallet Connected',
        message: 'Successfully connected to your Solana wallet.',
        duration: 3000
      })
    }
  }, [wallet.isConnected, addNotification])

  if (!wallet.isConnected && !isDemoMode) {
    return (
      <Container>
        <WalletRequired>
          <h3>Connect Your Wallet</h3>
          <p>Please connect your Solana wallet to view your projects.</p>
          <ConnectButton onClick={openModal}>
            Connect Wallet
          </ConnectButton>
        </WalletRequired>
      </Container>
    )
  }

  return (
    <Container>
      <ContentWrapper>
        <Header>
          <Title>Your Projects</Title>
          <Controls>
            <SearchBar>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <SearchInput
                type="text"
                placeholder="Search projects..."
                value={search}
                onChange={handleSearch}
              />
            </SearchBar>

            <ViewToggle>
              <ToggleButton
                $active={view === 'grid'}
                onClick={() => handleViewChange('grid')}
                title="Grid view"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M4 4h4v4H4V4zm6 0h4v4h-4V4zm6 0h4v4h-4V4zM4 10h4v4H4v-4zm6 0h4v4h-4v-4zm6 0h4v4h-4v-4zM4 16h4v4H4v-4zm6 0h4v4h-4v-4zm6 0h4v4h-4v-4z"/>
                </svg>
              </ToggleButton>
              <ToggleButton
                $active={view === 'list'}
                onClick={() => handleViewChange('list')}
                title="List view"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z"/>
                </svg>
              </ToggleButton>
            </ViewToggle>
          </Controls>
        </Header>

        <FilterBar>
          <FilterSelect
            name="type"
            value={filters.type}
            onChange={handleFilterChange}
          >
            <option value="all">All Types</option>
            <option value="x_management">X Management</option>
            <option value="github">GitHub Repository</option>
            <option value="website">Website Development</option>
          </FilterSelect>

          <FilterSelect
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="draft">Draft</option>
          </FilterSelect>

          <FilterSelect
            name="date"
            value={filters.date}
            onChange={handleFilterChange}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </FilterSelect>
        </FilterBar>

        {Object.entries(categorizedProjects).map(([category, projects]) => (
          projects.length > 0 && (
            <div key={category}>
              <CategoryTitle>
                {category.charAt(0).toUpperCase() + category.slice(1)} Projects
              </CategoryTitle>
              <ProjectsGrid $view={view}>
                {projects.map(project => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onClick={setSelectedProject}
                  />
                ))}
              </ProjectsGrid>
            </div>
          )
        ))}
      </ContentWrapper>

      <ProjectDetails
        project={selectedProject}
        isOpen={!!selectedProject}
        onClose={() => setSelectedProject(null)}
        onAction={handleProjectAction}
      />
    </Container>
  )
}

export default Projects
